import React, { useState, useEffect } from 'react';
import { Play, X, MagnifyingGlass, Clock, CircleNotch } from '@phosphor-icons/react';
import { motion } from '@/lib/motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ContactSection } from '../components/ContactSection';
import { PageMeta } from '../components/PageMeta';
import { PageHero } from '../components/PageHero';
import { QuickActions } from '../components/QuickActions';
import { Breadcrumb } from '../components/Breadcrumb';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const videoCategories = [
  { id: 'all', label: 'ALL VIDEOS' },
  { id: 'Project Showcases', label: 'PROJECTS' },
  { id: 'Factory Tours', label: 'FACTORY TOURS' },
  { id: 'Manufacturing Process', label: 'MANUFACTURING' },
  { id: 'Installation Guides', label: 'INSTALLATION' },
  { id: 'Technical Guides', label: 'TECHNICAL' },
  { id: 'Company & Culture', label: 'COMPANY' },
];

export default function VideoGalleryPage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => { axios.get(`${API}/videos`).then(res => { setVideos(res.data); setLoading(false); }).catch(() => setLoading(false)); }, []);

  const filteredVideos = videos.filter(v => {
    const matchesCategory = selectedCategory === 'all' || v.category === selectedCategory;
    const matchesSearch = searchQuery === '' || v.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gallery">
      <PageMeta title="Video Gallery" description="Watch project showcases, manufacturing processes, and installation guides." />
      <Navbar />
      <QuickActions variant="sidebar" />
      <QuickActions variant="fab" />
      <PageHero 
        label="MEDIA LIBRARY" 
        title={<>VIDEO <span className="text-bronze">GALLERY</span></>} 
        description="Explore our projects, manufacturing processes, and installation guides through immersive video walkthroughs."
        backgroundImage="https://files.ihgbrands.com/ihg-ind/generated/video_quality.webp"
      />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-black/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-4">
          <Breadcrumb items={[{ label: 'Resources', href: '/resources' }]} currentPage="Videos" />
        </div>
      </div>

      {/* Filters */}
      <section className="sticky top-16 md:top-24 lg:top-28 z-30 bg-white border-b border-black/5 shadow-sm py-4">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 flex flex-col md:flex-row gap-4 md:items-center justify-between">
          <div className="flex gap-2 overflow-x-auto">
            {videoCategories.map(cat => (
              <button key={cat.id} onClick={() => setSelectedCategory(cat.id)} className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider whitespace-nowrap transition-colors ${selectedCategory === cat.id ? 'bg-obsidian text-white' : 'bg-gallery text-concrete hover:bg-obsidian/5'}`}>
                {cat.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-concrete" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search videos..." className="pl-10 pr-4 py-2 w-full md:w-64 text-sm border border-black/10 focus:border-obsidian focus:outline-none bg-gallery" />
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-4 border-b border-black/5 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
          <p className="font-body text-sm text-concrete">Showing <span className="font-semibold text-obsidian">{filteredVideos.length}</span> videos</p>
        </div>
      </section>

      {/* Grid */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
          {loading ? <div className="flex justify-center py-20"><CircleNotch weight="bold" className="w-8 h-8 animate-spin text-bronze" /></div> :
          filteredVideos.length === 0 ? <div className="text-center py-20"><p className="text-concrete">No videos found.</p></div> :
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {filteredVideos.map((video) => (
              <motion.div key={video.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="group cursor-pointer" onClick={() => setSelectedVideo(video)} data-testid={`video-card-${video.id}`}>
                <div className="relative aspect-video bg-gallery overflow-hidden mb-3">
                  width={640}
                  height={360}
                  <img loading="lazy" decoding="async" src={video.thumbnail} alt={video.title} width={640} height={360} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-obsidian/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-14 h-14 bg-white flex items-center justify-center"><Play weight="fill" className="w-6 h-6 text-obsidian ml-1" /></div>
                  </div>
                  <div className="absolute bottom-2 right-2 bg-obsidian/80 text-white text-xs px-2 py-1 flex items-center gap-1"><Clock weight="bold" className="w-3 h-3" />{video.duration}</div>
                </div>
                <span className="label-architectural text-bronze text-[10px] block mb-1">{video.category}</span>
                <h3 className="font-display text-sm font-bold uppercase mb-1 group-hover:text-bronze transition-colors line-clamp-2">{video.title}</h3>
                <p className="font-body text-xs text-concrete line-clamp-2">{video.description}</p>
              </motion.div>
            ))}
          </div>}
        </div>
      </section>

      {/* Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 bg-obsidian/90 z-50 flex items-center justify-center p-4" onClick={() => setSelectedVideo(null)}>
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSelectedVideo(null)} className="absolute -top-12 right-0 text-white hover:text-bronze"><X weight="bold" className="w-8 h-8" /></button>
            <div className="aspect-video bg-obsidian"><iframe src={`https://www.youtube.com/embed/${selectedVideo.youtubeId}?autoplay=1`} title={selectedVideo.title} className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>
            <div className="bg-white p-5"><h3 className="font-display text-lg font-bold uppercase mb-2">{selectedVideo.title}</h3><p className="font-body text-sm text-concrete">{selectedVideo.description}</p></div>
          </div>
        </div>
      )}

      <ContactSection />
      <Footer />
    </div>
  );
}
